import React, { useContext, useEffect, useState } from "react";
import { isEmpty } from "lodash";
import { UserContext } from "../../context/auth";
import Headline from "../Headline";
import { BasicTable, Spinner, Icon } from "@px/px_design_system";
import {
  constructAdminAPIEndPoint,
  fetchHandler,
  getUTCDateTime,
  showCustomTooltip,
} from "../../../../utils";
import { getEmbedUrl } from "../../../../utils/embedUtils";
import DisplayError from "../../DisplayError";
import settings from "../../../../settings";
import "./style.css";

const CustomReporting = (props) => {
  const { data, isInvalidSuperUser } = useContext(UserContext);
  const { orgData, userData } = data;
  const { history } = props;
  const { type, id } = props.match?.params; // Get type (dashboards-next or looks) and id from the URL
  const [folderIds, setFolderIds] = useState({ user_folder_id: null });
  const [myFolderData, setMyFolderData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [globalFilter, setGlobalFilter] = useState("");
  const [embedUrl, setEmbedUrl] = useState("");
  const [isIframeLoaded, setIframeLoaded] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const initializeFolders = async () => {
      const isNonProd = settings.PORTAL_ENV !== "prod";
      try {
        setLoading(true); // Start spinner
        const response = await fetchHandler({
          method: "POST",
          url: constructAdminAPIEndPoint({ url: "looker/folders" }),
          body: {
            first_name: isNonProd
              ? userData[0].name.split(".")[0]
              : orgData.name,
            last_name: isNonProd ? userData[0].name.split(".")[1] : "Embed",
            external_group_id: orgData.name,
          },
        });

        if (response) {
          if (!response.user_folder_id) {
            throw new Error("User folder ID not found.");
          }
          setFolderIds({ user_folder_id: response.user_folder_id });
        }
      } catch (error) {
        console.error("Error initializing folders:", error);
        setLoading(false); // Only set loading to false on error
      }
    };

    if (!isEmpty(orgData) && !isEmpty(userData)) {
      initializeFolders();
    }
  }, [orgData, userData]);

  useEffect(() => {
    const fetchFolderData = async () => {
      if (!folderIds.user_folder_id) return;

      try {
        const myFolderResponse = await fetchHandler({
          method: "GET",
          url: constructAdminAPIEndPoint({
            url: `looker/folders/${folderIds.user_folder_id}`,
          }),
        });

        if (myFolderResponse) {
          const transformedData = {
            ...myFolderResponse,
            parent_folder_details: {
              ...myFolderResponse.parent_folder_details,
              dashboards: myFolderResponse.parent_folder_details.dashboards.map(
                (d) => ({
                  ...d,
                  type: "Dashboard",
                  redirectUrl: "dashboards-next",
                })
              ),
              looks: myFolderResponse.parent_folder_details.looks.map((l) => ({
                ...l,
                type: "Look",
                redirectUrl: "looks",
              })),
            },
          };
          setMyFolderData(transformedData);
        }
      } catch (error) {
        console.error("Error fetching folder data:", error);
      } finally {
        setLoading(false); // Stop spinner after both APIs are done
      }
    };

    fetchFolderData();
  }, [folderIds]);

  const getEmbedUrlForDashboard = async (
    dashboardId,
    redirectUrl,
    userData,
    orgData,
    isInvalidSuperUser
  ) => {
    const dashboard_url = `/embed/${redirectUrl}/${dashboardId}?embed_domain=${window.location.origin}&Currency=${orgData.config.allowed_currency}`;

    return getEmbedUrl({
      userData,
      orgData,
      isInvalidSuperUser,
      embedPath: dashboard_url,
    });
  };

  const fetchEmbedUrl = async (dashboardId, redirectUrl) => {
    try {
      setError(null); // Clear any previous errors
      setLoading(true); // Set loading to true before fetching embed URL
      const url = await getEmbedUrlForDashboard(
        dashboardId,
        redirectUrl,
        userData,
        orgData,
        isInvalidSuperUser
      );
      if (url) {
        setEmbedUrl(url);
        setIframeLoaded(false);
      }
    } catch (error) {
      console.error("Error getting embed URL:", error);
      setError("Failed to load dashboard");
    } finally {
      setLoading(false); // Set loading to false after embed URL is fetched
    }
  };

  useEffect(() => {
    if (type && id) {
      fetchEmbedUrl(id, type);
    }
  }, [type, id]);

  const handleDashboardClick = (dashboard) => {
    history.push(
      `/orgs/${orgData.id}/reporting/custom/reports/${dashboard.redirectUrl}/${dashboard.id}`
    );
  };

  const handleGoBack = () => {
    history.push(`/orgs/${orgData.id}/reporting/custom/reports`);
    setEmbedUrl("");
    setIframeLoaded(false);
  };

  const renderDashboardTable = (data) => {
    if (!data?.parent_folder_details?.dashboards?.length) {
      return <DisplayError title="No Report Found" />;
    }

    return (
      <div className="custom--dashboard-container">
        <strong className="d-block fw-semibold mb-2">
          About Custom Reports:
        </strong>
        <ul className="mb-2 ps-4">
          <li className="mb-2">
            This page displays all your saved <strong>Looks</strong> and{" "}
            <strong>Dashboards</strong>.
          </li>
          <li className="mb-2">
            Create new reports from the <strong>Explore</strong> page.
          </li>
          <li>Click on any report below to view and edit it.</li>
        </ul>
        <BasicTable
          table_columns={[
            {
              accessorFn: (row) => row.title,
              id: "title",
              header: "Name",
              Cell: ({ row }) => {
                const dashboard = row.original;
                return (
                  <button
                    type="button"
                    className="dashboard-link"
                    onClick={() => handleDashboardClick(dashboard)}
                  >
                    {dashboard.title}
                  </button>
                );
              },
            },
            {
              accessorFn: (row) => row.type,
              id: "type",
              header: "Type",
            },
            {
              accessorFn: (row) => row.updated_at,
              id: "updated_at",
              header: "Last Modified",
              Cell: ({ cell }) => {
                return showCustomTooltip(
                  getUTCDateTime(cell.getValue()),
                  "Time shown in UTC"
                );
              },
            },
          ]}
          table_data={[
            ...data.parent_folder_details.dashboards,
            ...data.parent_folder_details.looks,
          ]}
          loading={loading}
          errorNoResult={
            globalFilter
              ? null
              : {
                  title: "No Dashboards Found",
                  description:
                    "There are no dashboards available in this folder.",
                }
          }
          state={{
            globalFilter,
          }}
          onGlobalFilterChange={setGlobalFilter}
          muiPaginationProps={{
            showRowsPerPage: false,
          }}
          initialStateOptions={{
            sorting: [
              {
                id: "updated_at",
                desc: true,
              },
            ],
          }}
        />
      </div>
    );
  };

  const getContent = () => {
    let content = null;

    if (loading) {
      content = (
        <div className="custom--dashboard-container">
          <Spinner
            className="spinner-icon"
            color="primary"
            loadingtext="Loading"
            spinnerposition="container"
            data-testid="spinner-loading"
          />
        </div>
      );
    } else if (error) {
      content = (
        <div className="custom--dashboard-container">
          <DisplayError title={error} typeOfError="error" />
        </div>
      );
    } else if (!type || !id) {
      content = renderDashboardTable(myFolderData);
    } else if (!isEmpty(embedUrl)) {
      content = (
        <div className="custom--dashboard">
          {!isIframeLoaded && (
            <Spinner
              className="spinner-icon"
              color="primary"
              loadingtext="Loading"
              spinnerposition="container"
              data-testid="spinner-loading"
            />
          )}
          <div className="go__back--link">
            <Icon
              icon={["fas", "times"]}
              style={{ color: "rgba(128, 128, 128, 1)" }}
              size="2x"
              onClick={handleGoBack}
              data-testid="go-back-button"
            />
          </div>
          <iframe
            id="embed--reporting"
            className={`custom--dashboard-iframe ${
              !isIframeLoaded ? "hide" : ""
            }`}
            src={embedUrl}
            title="reporting"
            onLoad={() => setIframeLoaded(true)}
            data-testid="iframe-reporting"
          />
        </div>
      );
    }

    return content;
  };

  return (
    <>
      <Headline>Custom Reports</Headline>
      <div className="wrapper">{getContent()}</div>
    </>
  );
};

export default CustomReporting;
